import { useMemo, useState } from "react";
import { Box, Typography, Button, useTheme } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ReferenceLine,
  ReferenceArea,
  ResponsiveContainer,
  CartesianGrid,
  Legend,
} from "recharts";
import { PanelShell, StatGrid, StatTile, PanelUnavailable, FindingList } from "./PanelShell";

const AXIS_COLOURS = { x: "#E7C25B", y: "#3a8eff", z: "#FF7A2F" };

/**
 * The gyro spectrum with the filter stack drawn over it.
 *
 * The comparison is the point, so pre-filter, estimated post-filter and the
 * filter's own response share one frequency axis: a notch sitting away from the
 * measured peak is visible rather than described.
 */
const FilterPanel = ({ module }) => {
  const theme = useTheme();
  const [splitAxes, setSplitAxes] = useState(false);
  const panel = module?.panel;

  const rows = useMemo(() => {
    if (!panel) return [];
    return panel.frequencies.map((f, i) => ({
      freq: f,
      pre: panel.pre_filter_db[i],
      post: panel.post_filter_db[i],
      response: panel.filter_response_db[i],
      x: panel.per_axis_db?.x?.[i],
      y: panel.per_axis_db?.y?.[i],
      z: panel.per_axis_db?.z?.[i],
    }));
  }, [panel]);

  if (!panel) return <PanelUnavailable reason={module?.skip_reason || "No gyro spectrum for this log."} />;

  const fromBatch = panel.source === "batch_sampler";

  return (
    <PanelShell
      title="Gyro Noise & Filters"
      subtitle={
        `Spectrum from ${fromBatch ? "the IMU batch sampler" : "the logged IMU rate"} at ` +
        `${panel.sample_rate_hz} Hz — visible band 0–${panel.nyquist_hz} Hz.`
      }
      health={module.health}
    >
      <StatGrid>
        <StatTile label="Noise peak" value={panel.peak_hz ?? "—"} unit={panel.peak_hz ? "Hz" : ""} />
        <StatTile
          label="Attenuation at peak"
          value={panel.peak_attenuation_db}
          unit="dB"
          tone={panel.peak_attenuation_db >= 10 ? "good" : panel.peak_attenuation_db < 3 ? "warn" : "neutral"}
        />
        <StatTile
          label="Gyro low-pass"
          value={panel.lpf_cutoff_hz || "off"}
          unit={panel.lpf_cutoff_hz ? "Hz" : ""}
        />
        <StatTile
          label="Notch tracking"
          value={panel.notch_tracking === "none" ? "disabled" : panel.notch_tracking}
          tone={panel.notch_tracking === "none" ? "warn" : "neutral"}
        />
        <StatTile
          label="Analysis band"
          value={`0–${panel.nyquist_hz}`}
          unit="Hz"
          tone={fromBatch ? "good" : "neutral"}
          hint={fromBatch ? undefined : "Set INS_LOG_BAT_MASK for full-band visibility"}
        />
      </StatGrid>

      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="caption" sx={{ color: theme.palette.text.secondary, textTransform: "uppercase" }}>
          Power spectral density
        </Typography>
        <Button size="small" onClick={() => setSplitAxes((v) => !v)} sx={{ textTransform: "none" }}>
          {splitAxes ? "Show combined" : "Split by axis"}
        </Button>
      </Box>

      <Box sx={{ height: 320 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={rows} margin={{ top: 8, right: 8, left: -12, bottom: 4 }}>
            <CartesianGrid stroke="rgba(255,255,255,0.05)" />
            <XAxis
              dataKey="freq"
              type="number"
              domain={[0, panel.nyquist_hz]}
              tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
              label={{ value: "Hz", position: "insideBottomRight", offset: -2, fontSize: 10 }}
            />
            <YAxis yAxisId="psd" tick={{ fontSize: 10, fill: theme.palette.text.secondary }} />
            {/* The filter response is a gain, not a power level — its own axis
                keeps it legible instead of pinned to the top of the PSD. */}
            <YAxis yAxisId="resp" orientation="right" domain={[-60, 5]} tick={{ fontSize: 10, fill: "rgba(255,255,255,0.25)" }} />
            <Tooltip
              contentStyle={{ background: "#171717", border: "1px solid rgba(255,255,255,0.12)", fontSize: 11 }}
              labelFormatter={(l) => `${Number(l).toFixed(1)} Hz`}
              formatter={(v, name) => [`${Number(v).toFixed(1)} dB`, name]}
            />
            <Legend wrapperStyle={{ fontSize: 11 }} />

            {panel.notch_centres_hz.map((c) => (
              <ReferenceLine
                key={c}
                yAxisId="psd"
                x={c}
                stroke="#3a8eff"
                strokeDasharray="4 4"
                label={{ value: `notch ${c}`, fill: "#3a8eff", fontSize: 9, position: "top" }}
              />
            ))}
            {panel.peak_hz && (
              <ReferenceArea
                yAxisId="psd"
                x1={Math.max(0, panel.peak_hz * 0.97)}
                x2={panel.peak_hz * 1.03}
                fill="#FF7A2F"
                fillOpacity={0.12}
              />
            )}

            {splitAxes ? (
              ["x", "y", "z"].map((axis) => (
                <Line
                  key={axis}
                  yAxisId="psd"
                  type="monotone"
                  dataKey={axis}
                  name={`gyro ${axis}`}
                  stroke={AXIS_COLOURS[axis]}
                  strokeWidth={1.2}
                  dot={false}
                  isAnimationActive={false}
                />
              ))
            ) : (
              <>
                <Line yAxisId="psd" type="monotone" dataKey="pre" name="pre-filter" stroke="#E7C25B"
                      strokeWidth={1.6} dot={false} isAnimationActive={false} />
                <Line yAxisId="psd" type="monotone" dataKey="post" name="post-filter (estimated)" stroke="#4ADE80"
                      strokeWidth={1.6} dot={false} isAnimationActive={false} />
                <Line yAxisId="resp" type="monotone" dataKey="response" name="filter response"
                      stroke="rgba(255,255,255,0.35)" strokeWidth={1} strokeDasharray="3 3" dot={false}
                      isAnimationActive={false} />
              </>
            )}
          </LineChart>
        </ResponsiveContainer>
      </Box>

      <Typography variant="caption" sx={{ display: "block", mt: 1.5, color: theme.palette.text.secondary }}>
        The dashed line is what the configured filters do to each frequency; green is the spectrum after
        applying it. A resonance the notch sits on gets pulled down — one it misses passes straight through
        into the rate controller.
        {!fromBatch &&
          ` This log has no batch-sampler data, so nothing above ${panel.nyquist_hz} Hz is visible here — absent, not proven quiet.`}
      </Typography>

      <FindingList findings={module.findings} />
    </PanelShell>
  );
};

export default FilterPanel;
